// Import/export helpers for the SEO Pages section.
//
// Export/import are done client-side against the existing seoPages CRUD
// endpoints (no new backend needed): a row with `_id` is PATCHed, a row without
// one is created. `SEO_TEMPLATE` ships the recommended bilingual keywords/titles
// from kayfa-seo-keywords-ar-en.docx, keyed by the fixed seoPages ObjectIds so
// importing it updates the live pages directly.

export type SeoImportRow = {
  _id?: string;
  page?: string; // human hint only (ignored by the backend)
  title?: string;
  title_ar?: string;
  description?: string;
  description_ar?: string;
  keywords?: string[] | string;
  keywords_ar?: string[] | string;
};

// Coerce a keywords value (array OR comma-separated string) into a clean array.
export function normalizeKeywords(v: unknown): string[] {
  if (Array.isArray(v)) return v.map((s) => String(s).trim()).filter(Boolean);
  if (typeof v === 'string') return v.split(',').map((s) => s.trim()).filter(Boolean);
  return [];
}

// Build the payload sent to create/update a seoPages record from an import row.
export function toSeoPayload(row: SeoImportRow) {
  return {
    title: (row.title ?? '').trim(),
    title_ar: (row.title_ar ?? '').trim(),
    description: (row.description ?? '').trim(),
    description_ar: (row.description_ar ?? '').trim(),
    keywords: normalizeKeywords(row.keywords),
    keywords_ar: normalizeKeywords(row.keywords_ar),
  };
}

// Trigger a JSON file download in the browser.
export function downloadJson(data: unknown, filename: string) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Recommended SEO per marketing page (from kayfa-seo-keywords-ar-en.docx).
// `_id`s are the fixed seoPages ObjectIds the app reads (see kayfa-nextjs
// src/utils/seo.ts SEO_PAGE_IDS) so importing this updates the real records.
export const SEO_TEMPLATE: SeoImportRow[] = [
  {
    _id: '654a4211ffd759d1d66fba35',
    page: 'Home',
    title: 'Kayfa Academy | Accredited Arabic tech courses to learn programming',
    title_ar: 'أكاديمية كيف | كورسات تقنية معتمدة بالعربي لتعلم البرمجة والتقنية',
    description:
      'Learn programming and tech skills in Arabic with accredited, certified courses on Kayfa Academy.',
    description_ar:
      'تعلّم البرمجة والمهارات التقنية بالعربية عبر دورات معتمدة وشهادات موثّقة على أكاديمية كيف.',
    keywords: [
      'Kayfa',
      'Kayfa Academy',
      'online courses Arabic',
      'learn programming Arabic',
      'accredited tech courses',
      'Arabic e-learning platform',
      'certified online courses',
    ],
    keywords_ar: [
      'أكاديمية كيف',
      'كورسات أونلاين بالعربي',
      'تعلم البرمجة بالعربي',
      'دورات تقنية معتمدة',
      'منصة تعليم تقنية عربية',
      'كورسات بشهادة معتمدة',
    ],
  },
  {
    _id: '654a4130ffd759d1d66fba21',
    page: 'About Us',
    title: 'About Kayfa Academy | Your accredited Arabic tech learning platform',
    title_ar: 'عن أكاديمية كيف | منصتك لتعلم التقنية بشهادات معتمدة',
    description: 'Learn about Kayfa Academy, an accredited Arabic tech education platform.',
    description_ar: 'تعرّف على أكاديمية كيف، منصة تعليم تقنية عربية معتمدة.',
    keywords: [
      'about Kayfa Academy',
      'Kayfa about us',
      'accredited tech academy',
      'Arabic edtech platform',
    ],
    keywords_ar: [
      'عن أكاديمية كيف',
      'من نحن كيف',
      'منصة تعليم تقنية',
      'أكاديمية تقنية معتمدة',
      'اعتماد كيف',
    ],
  },
  {
    _id: '654a40bcffd759d1d66fba1b',
    page: 'Instructors',
    title: 'Kayfa Academy Instructors | Expert tech professionals',
    title_ar: 'مدربو أكاديمية كيف | خبراء ومحترفون في مجال التقنية',
    description: 'Meet the expert, certified instructors teaching on Kayfa Academy.',
    description_ar: 'تعرّف على المدربين الخبراء والمعتمدين في أكاديمية كيف.',
    keywords: [
      'Kayfa instructors',
      'tech experts',
      'certified trainers',
      'programming mentors',
    ],
    keywords_ar: [
      'مدربو كيف',
      'خبراء تقنية',
      'محاضرون معتمدون',
      'مدربون بالعربي',
      'خبراء برمجة',
    ],
  },
  {
    _id: '654a40ebffd759d1d66fba1e',
    page: 'Blog',
    title: 'Kayfa Blog | Arabic tech articles and tips',
    title_ar: 'مدونة كيف | مقالات ونصائح تقنية بالعربي',
    description: 'Read the latest Arabic tech articles, programming tips and insights from Kayfa.',
    description_ar: 'اقرأ أحدث المقالات والنصائح التقنية بالعربية من أكاديمية كيف.',
    keywords: [
      'Kayfa blog',
      'Arabic tech articles',
      'programming tips',
      'cybersecurity articles',
      'web development blog',
    ],
    keywords_ar: [
      'مدونة كيف',
      'مقالات تقنية بالعربي',
      'نصائح برمجة',
      'مقالات الأمن السيبراني',
      'مقالات تطوير الويب',
    ],
  },
  {
    _id: '654a4151ffd759d1d66fba24',
    page: 'Contact',
    title: 'Contact Kayfa Academy | Support & enquiries',
    title_ar: 'تواصل مع أكاديمية كيف | الدعم والاستفسارات',
    description: 'Get in touch with Kayfa Academy for support and course enquiries.',
    description_ar: 'تواصل مع أكاديمية كيف للدعم والاستفسار عن الدورات.',
    keywords: ['contact Kayfa', 'Kayfa support', 'course enquiries'],
    keywords_ar: ['تواصل مع كيف', 'دعم كيف', 'استفسارات الدورات', 'خدمة عملاء كيف'],
  },
];
